import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import { assessmentUpdateNotifier } from '../utils/assessmentUpdateNotifier';

interface AssessmentResponse {
  questionId: string;
  answer: number;
  timestamp: Date;
}

interface OccupationalData {
  responses: AssessmentResponse[];
  scores: {
    overall: number;
    category1: number; // CBI
    category2: number; // UWES
    category3: number; // Employee Engagement & Climate
  };
  results: {
    level: 'Low' | 'Moderate' | 'High';
    interpretation: string;
    recommendations: string[];
  };
}

const OccupationalAssessmentFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [responses, setResponses] = useState<AssessmentResponse[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculate total questions based on selected category
  const getTotalQuestions = () => {
    if (!selectedCategory) {
      return 1; // Just the category selection question
    }
    if (selectedCategory === 1) {
      return 19; // CBI has 19 questions (6 personal + 7 work-related + 6 client-related)
    }
    if (selectedCategory === 2) {
      return 17; // UWES has 17 questions (6 vigor + 5 dedication + 6 absorption)
    }
    if (selectedCategory === 3) {
      return 17; // Employee Engagement Survey has 17 questions (16 Likert + 1 NPS)
    }
    return 5;
  };
  
  const totalQuestions = getTotalQuestions();

  // Category selection question
  const categorySelectionQuestion = {
    id: '0',
    question: 'Which Occupational assessment would you like to take?',
    type: 'selection' as const,
    options: [
      'CBI (Copenhagen Burnout Inventory)',
      'UWES (Utrecht Work Engagement Scale)',
      'Employee Engagement & Climate Survey'
    ]
  };

  // Assessment questions organized by category
  const categoryQuestions: { [key: number]: Array<{ id: string; question: string; category: number; type: string; scaleType?: string }> } = {
    1: [
      // CBI (Copenhagen Burnout Inventory) - 19 items
      // Part 1: Personal Burnout (6 items)
      { id: '1', question: 'How often do you feel tired?', category: 1, type: 'frequency', scaleType: 'frequency' },
      { id: '2', question: 'How often are you physically exhausted?', category: 1, type: 'frequency', scaleType: 'frequency' },
      { id: '3', question: 'How often are you emotionally exhausted?', category: 1, type: 'frequency', scaleType: 'frequency' },
      { id: '4', question: 'How often do you think: "I can\'t take it anymore"?', category: 1, type: 'frequency', scaleType: 'frequency' },
      { id: '5', question: 'How often do you feel worn out?', category: 1, type: 'frequency', scaleType: 'frequency' },
      { id: '6', question: 'How often do you feel weak and susceptible to illness?', category: 1, type: 'frequency', scaleType: 'frequency' },
      
      // Part 2: Work-related Burnout (7 items)
      { id: '7', question: 'Do you feel worn out at the end of the working day?', category: 1, type: 'frequency', scaleType: 'frequency' },
      { id: '8', question: 'Are you exhausted in the morning at the thought of another day at work?', category: 1, type: 'frequency', scaleType: 'frequency' },
      { id: '9', question: 'Do you feel that every working hour is tiring for you?', category: 1, type: 'frequency', scaleType: 'frequency' },
      { id: '10', question: 'Do you have enough energy for family and friends during leisure time?', category: 1, type: 'frequency', scaleType: 'frequency-reverse' },
      { id: '11', question: 'Is your work emotionally exhausting?', category: 1, type: 'degree', scaleType: 'degree' },
      { id: '12', question: 'Does your work frustrate you?', category: 1, type: 'degree', scaleType: 'degree' },
      { id: '13', question: 'Do you feel burnt out because of your work?', category: 1, type: 'degree', scaleType: 'degree' },
      
      // Part 3: Client-related Burnout (6 items)
      { id: '14', question: 'Do you find it hard to work with clients?', category: 1, type: 'degree', scaleType: 'degree' },
      { id: '15', question: 'Do you find it frustrating to work with clients?', category: 1, type: 'degree', scaleType: 'degree' },
      { id: '16', question: 'Does it drain your energy to work with clients?', category: 1, type: 'degree', scaleType: 'degree' },
      { id: '17', question: 'Do you feel that you give more than you get back when you work with clients?', category: 1, type: 'degree', scaleType: 'degree' },
      { id: '18', question: 'Are you tired of working with clients?', category: 1, type: 'degree', scaleType: 'degree' },
      { id: '19', question: 'Do you sometimes wonder how long you will be able to continue working with clients?', category: 1, type: 'degree', scaleType: 'degree' }
    ],
    2: [
      // UWES (Utrecht Work Engagement Scale) - 17 items
      // Vigor (6 items)
      { id: '1', question: 'At my work, I feel bursting with energy.', category: 2, type: 'frequency', scaleType: 'uwes' },
      { id: '2', question: 'At my job, I feel strong and vigorous.', category: 2, type: 'frequency', scaleType: 'uwes' },
      { id: '3', question: 'When I get up in the morning, I feel like going to work.', category: 2, type: 'frequency', scaleType: 'uwes' },
      { id: '4', question: 'I can continue working for very long periods at a time.', category: 2, type: 'frequency', scaleType: 'uwes' },
      { id: '5', question: 'At my job, I am very resilient, mentally.', category: 2, type: 'frequency', scaleType: 'uwes' },
      { id: '6', question: 'At my work, I always persevere, even when things do not go well.', category: 2, type: 'frequency', scaleType: 'uwes' },
      
      // Dedication (5 items)
      { id: '7', question: 'I find the work that I do full of meaning and purpose.', category: 2, type: 'frequency', scaleType: 'uwes' },
      { id: '8', question: 'I am enthusiastic about my job.', category: 2, type: 'frequency', scaleType: 'uwes' },
      { id: '9', question: 'My job inspires me.', category: 2, type: 'frequency', scaleType: 'uwes' },
      { id: '10', question: 'I am proud of the work that I do.', category: 2, type: 'frequency', scaleType: 'uwes' },
      { id: '11', question: 'To me, my job is challenging.', category: 2, type: 'frequency', scaleType: 'uwes' },
      
      // Absorption (6 items)
      { id: '12', question: 'Time flies when I am working.', category: 2, type: 'frequency', scaleType: 'uwes' },
      { id: '13', question: 'When I am working, I forget everything else around me.', category: 2, type: 'frequency', scaleType: 'uwes' },
      { id: '14', question: 'I feel happy when I am working intensely.', category: 2, type: 'frequency', scaleType: 'uwes' },
      { id: '15', question: 'I am immersed in my work.', category: 2, type: 'frequency', scaleType: 'uwes' },
      { id: '16', question: 'I get carried away when I am working.', category: 2, type: 'frequency', scaleType: 'uwes' },
      { id: '17', question: 'It is difficult to detach myself from my job.', category: 2, type: 'frequency', scaleType: 'uwes' }
    ],
    3: [
      // Employee Engagement Survey - 16 Likert items (as per specification)
      // Job Satisfaction & Meaning (4 items)
      { id: '1', question: 'I am satisfied with my job overall.', category: 3, type: 'likert', scaleType: 'agreement' },
      { id: '2', question: 'I feel valued and recognized for my contributions at work.', category: 3, type: 'likert', scaleType: 'agreement' },
      { id: '3', question: 'My work is meaningful and fulfilling.', category: 3, type: 'likert', scaleType: 'agreement' },
      { id: '4', question: 'I have a healthy work-life balance.', category: 3, type: 'likert', scaleType: 'agreement' },

      // Leadership & Support (4 items)
      { id: '5', question: 'I have confidence in the leadership team\'s ability to make the right decisions.', category: 3, type: 'likert', scaleType: 'agreement' },
      { id: '6', question: 'Leadership communicates a clear vision and direction for the company.', category: 3, type: 'likert', scaleType: 'agreement' },
      { id: '7', question: 'My direct manager supports me in my role.', category: 3, type: 'likert', scaleType: 'agreement' },
      { id: '8', question: 'Leadership listens to and acts on employee feedback.', category: 3, type: 'likert', scaleType: 'agreement' },

      // Growth & Development (3 items)
      { id: '9', question: 'I see a clear path for career growth within the organization.', category: 3, type: 'likert', scaleType: 'agreement' },
      { id: '10', question: 'I have access to the learning and development resources I need to grow.', category: 3, type: 'likert', scaleType: 'agreement' },
      { id: '11', question: 'I receive regular feedback that helps me improve and grow in my role.', category: 3, type: 'likert', scaleType: 'agreement' },

      // Work Environment (5 items)
      { id: '12', question: 'I have enough flexibility in my role to manage personal and professional responsibilities.', category: 3, type: 'likert', scaleType: 'agreement' },
      { id: '13', question: 'I feel a sense of belonging at work.', category: 3, type: 'likert', scaleType: 'agreement' },
      { id: '14', question: 'I rarely feel stressed or overwhelmed at work.', category: 3, type: 'likert', scaleType: 'agreement' },
      { id: '15', question: 'I feel comfortable taking time off when needed.', category: 3, type: 'likert', scaleType: 'agreement' },
      { id: '16', question: 'All employees are treated fairly and with respect in this organization.', category: 3, type: 'likert', scaleType: 'agreement' },
      
      // Final Question: Net Promoter Score (1 item)
      { id: '34', question: 'On a scale of 0-10, how likely are you to recommend your company to your family or friends?', category: 3, type: 'nps', scaleType: 'nps' }
    ]
  };

  // Get current questions based on selected category
  const getCurrentQuestions = () => {
    if (!selectedCategory) {
      return [categorySelectionQuestion];
    }
    // When category is selected, only return category-specific questions
    return categoryQuestions[selectedCategory as keyof typeof categoryQuestions] || [];
  };

  const questions = getCurrentQuestions();

  const currentQuestionData = questions[currentQuestion - 1];
  const currentResponse = currentQuestionData ? responses.find(r => r.questionId === currentQuestionData.id) : null;
  const progress = (currentQuestion / totalQuestions) * 100;

  // Get answer options based on selected category and question type
  const getAnswerOptions = () => {
    if (selectedCategory === 1) {
      // CBI uses different scales based on scaleType
      const question = categoryQuestions[1]?.find(q => q.id === currentQuestionData?.id);
      
      if (question?.scaleType === 'frequency' || question?.scaleType === 'frequency-reverse') {
        // Frequency scale: Always, Often, Sometimes, Seldom, Never/almost never
        // Scoring: Always=100, Often=75, Sometimes=50, Seldom=25, Never=0
        return [
          { value: 100, label: 'Always', score: 100 },
          { value: 75, label: 'Often', score: 75 },
          { value: 50, label: 'Sometimes', score: 50 },
          { value: 25, label: 'Seldom', score: 25 },
          { value: 0, label: 'Never/almost never', score: 0 }
        ];
      } else if (question?.scaleType === 'degree') {
        // Degree scale: To a very high degree, To a high degree, Somewhat, To a low degree, To a very low degree
        // Scoring: Very high=100, High=75, Somewhat=50, Low=25, Very low=0
        return [
          { value: 100, label: 'To a very high degree', score: 100 },
          { value: 75, label: 'To a high degree', score: 75 },
          { value: 50, label: 'Somewhat', score: 50 },
          { value: 25, label: 'To a low degree', score: 25 },
          { value: 0, label: 'To a very low degree', score: 0 }
        ];
      }
    } else if (selectedCategory === 2) {
      // UWES uses 0-6 frequency scale
      return [
        { value: 0, label: 'Never', score: 0 },
        { value: 1, label: 'Almost never (a few times a year or less)', score: 1 },
        { value: 2, label: 'Rarely (once a month or less)', score: 2 },
        { value: 3, label: 'Sometimes (a few times a month)', score: 3 },
        { value: 4, label: 'Often (once a week)', score: 4 },
        { value: 5, label: 'Very often (a few times a week)', score: 5 },
        { value: 6, label: 'Always (every day)', score: 6 }
      ];
    } else if (selectedCategory === 3) {
      // Employee Engagement & Climate uses different scales
      const question = categoryQuestions[3]?.find(q => q.id === currentQuestionData?.id);
      
      if (question?.scaleType === 'nps') {
        // Net Promoter Score: 0-10 scale
        return [
          { value: 0, label: '0', score: 0 },
          { value: 1, label: '1', score: 1 },
          { value: 2, label: '2', score: 2 },
          { value: 3, label: '3', score: 3 },
          { value: 4, label: '4', score: 4 },
          { value: 5, label: '5', score: 5 },
          { value: 6, label: '6', score: 6 },
          { value: 7, label: '7', score: 7 },
          { value: 8, label: '8', score: 8 },
          { value: 9, label: '9', score: 9 },
          { value: 10, label: '10', score: 10 }
        ];
      } else {
        // Agreement scale (1-5)
        return [
          { value: 1, label: 'Strongly Disagree', score: 1 },
          { value: 2, label: 'Disagree', score: 2 },
          { value: 3, label: 'Neither Agree nor Disagree', score: 3 },
          { value: 4, label: 'Agree', score: 4 },
          { value: 5, label: 'Strongly Agree', score: 5 }
        ];
      }
    }
    return [];
  };

  const answerOptions = getAnswerOptions();

  // Type guard to check if question has options (selection type)
  const isSelectionQuestion = (question: any): question is { id: string; question: string; type: 'selection'; options: string[] } => {
    return question.type === 'selection' && 'options' in question;
  };

  // Type guard to check if question has category (any assessment question type)
  const isAssessmentQuestion = (question: any): question is { id: string; question: string; category: number; type: string; scaleType?: string } => {
    return 'category' in question && question.type !== 'selection' && question.type !== 'nps';
  };

  // Type guard for NPS question
  const isNPSQuestion = (question: any): question is { id: string; question: string; category: number; type: 'nps'; scaleType: 'nps' } => {
    return question.type === 'nps' && question.scaleType === 'nps';
  };

  const getCategoryName = (categoryNumber: number) => {
    switch (categoryNumber) {
      case 1: return 'CBI (Copenhagen Burnout Inventory)';
      case 2: return 'UWES (Utrecht Work Engagement Scale)';
      case 3: return 'Employee Engagement & Climate Survey';
      default: return 'Unknown Category';
    }
  };

  const saveCurrentResponse = (answer: number) => {
    if (!currentQuestionData) return;
    
    const newResponse: AssessmentResponse = {
      questionId: currentQuestionData.id,
      answer,
      timestamp: new Date()
    };

    setResponses(prev => {
      const filtered = prev.filter(r => r.questionId !== currentQuestionData.id);
      return [...filtered, newResponse];
    });

    // If this is the category selection question, set the selected category
    if (currentQuestionData.id === '0') {
      const categoryMap = [1, 2, 3]; // Map selection index to category number
      setSelectedCategory(categoryMap[answer]);
      // Reset to question 1 for the selected category
      setCurrentQuestion(1);
    }
  };

  const handleNext = () => {
    if (!selectedCategory && currentQuestion === 1) {
      // On category selection page, move to first question of selected category
      setCurrentQuestion(1);
      return;
    }
    if (currentQuestion < totalQuestions) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentQuestion > 1) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const calculateScores = (responses: AssessmentResponse[], selectedCategory: number) => {
    // Filter out the category selection response (id: '0')
    const assessmentResponses = responses.filter(r => r.questionId !== '0');
    
    if (selectedCategory === 1) {
      // CBI scoring
      // Part 1: Personal Burnout (items 1-6)
      const personalBurnoutResponses = assessmentResponses.filter(r => {
        const questionNumber = parseInt(r.questionId);
        return questionNumber >= 1 && questionNumber <= 6;
      });
      const personalBurnoutAverage = personalBurnoutResponses.length >= 3 
        ? Math.round(personalBurnoutResponses.reduce((sum, r) => sum + r.answer, 0) / personalBurnoutResponses.length)
        : 0;
      
      // Part 2: Work-related Burnout (items 7-13)
      // Item 10 is reverse-scored
      const workRelatedResponses = assessmentResponses.filter(r => {
        const questionNumber = parseInt(r.questionId);
        return questionNumber >= 7 && questionNumber <= 13;
      });
      console.log('Work-related responses:', workRelatedResponses.map(r => ({ id: r.questionId, answer: r.answer })));

      const workRelatedSum = workRelatedResponses.reduce((sum, r) => {
        const questionNumber = parseInt(r.questionId);
        // Reverse score for item 10 (energy for family/friends)
        if (questionNumber === 10) {
          const reversedScore = 100 - r.answer;
          console.log(`Q10 reverse: ${r.answer} -> ${reversedScore}`);
          return sum + reversedScore;
        }
        return sum + r.answer;
      }, 0);
      
      const workRelatedAverage = workRelatedResponses.length >= 4 
        ? Math.round(workRelatedSum / workRelatedResponses.length)
        : 0;
      
      // Part 3: Client-related Burnout (items 14-19)
      const clientRelatedResponses = assessmentResponses.filter(r => {
        const questionNumber = parseInt(r.questionId);
        return questionNumber >= 14 && questionNumber <= 19;
      });
      const clientRelatedAverage = clientRelatedResponses.length >= 3 
        ? Math.round(clientRelatedResponses.reduce((sum, r) => sum + r.answer, 0) / clientRelatedResponses.length)
        : 0;
      
      // Overall CBI score is the average of all three parts
      const cbiOverall = Math.round((personalBurnoutAverage + workRelatedAverage + clientRelatedAverage) / 3);
      
      return {
        overall: cbiOverall,
        category1: cbiOverall,
        category2: 0,
        category3: 0,
        cbiTotal: cbiOverall,
        personalBurnout: personalBurnoutAverage,
        workRelatedBurnout: workRelatedAverage,
        clientRelatedBurnout: clientRelatedAverage
      };
    } else if (selectedCategory === 2) {
      // UWES scoring - 3 dimensions (keep 0-6 scale for assessment)
      // Vigor: Items 1-6
      const vigorResponses = assessmentResponses.filter(r => {
        const questionNumber = parseInt(r.questionId);
        return questionNumber >= 1 && questionNumber <= 6;
      });
      const vigorAverage = vigorResponses.length > 0 
        ? Math.round((vigorResponses.reduce((sum, r) => sum + r.answer, 0) / vigorResponses.length) * 100) / 100
        : 0;
      
      // Dedication: Items 7-11
      const dedicationResponses = assessmentResponses.filter(r => {
        const questionNumber = parseInt(r.questionId);
        return questionNumber >= 7 && questionNumber <= 11;
      });
      const dedicationAverage = dedicationResponses.length > 0 
        ? Math.round((dedicationResponses.reduce((sum, r) => sum + r.answer, 0) / dedicationResponses.length) * 100) / 100
        : 0;
      
      // Absorption: Items 12-17
      const absorptionResponses = assessmentResponses.filter(r => {
        const questionNumber = parseInt(r.questionId);
        return questionNumber >= 12 && questionNumber <= 17;
      });
      const absorptionAverage = absorptionResponses.length > 0 
        ? Math.round((absorptionResponses.reduce((sum, r) => sum + r.answer, 0) / absorptionResponses.length) * 100) / 100
        : 0;
      
      // Overall UWES score = average of all 17 items (0-6 scale)
      const uwesOverall = assessmentResponses.length > 0 
        ? Math.round((assessmentResponses.reduce((sum, r) => sum + r.answer, 0) / assessmentResponses.length) * 100) / 100
        : 0;
      
      return {
        overall: uwesOverall,
        category1: 0,
        category2: uwesOverall,
        category3: 0,
        uwesTotal: uwesOverall,
        vigorScore: vigorAverage,
        dedicationScore: dedicationAverage,
        absorptionScore: absorptionAverage
      };
    } else if (selectedCategory === 3) {
      // Employee Engagement Survey scoring - 4 sections (as per specification)
      // Job Satisfaction & Meaning (items 1-4) - 1-5 scale
      const jobSatisfactionResponses = assessmentResponses.filter(r => {
        const questionNumber = parseInt(r.questionId);
        return questionNumber >= 1 && questionNumber <= 4;
      });
      const jobSatisfactionAverage = jobSatisfactionResponses.length > 0
        ? Math.round((jobSatisfactionResponses.reduce((sum, r) => sum + r.answer, 0) / jobSatisfactionResponses.length) * 100) / 100
        : 0;

      // Leadership & Support (items 5-8) - 1-5 scale
      const leadershipResponses = assessmentResponses.filter(r => {
        const questionNumber = parseInt(r.questionId);
        return questionNumber >= 5 && questionNumber <= 8;
      });
      const leadershipAverage = leadershipResponses.length > 0
        ? Math.round((leadershipResponses.reduce((sum, r) => sum + r.answer, 0) / leadershipResponses.length) * 100) / 100
        : 0;

      // Growth & Development (items 9-11) - 1-5 scale
      const growthResponses = assessmentResponses.filter(r => {
        const questionNumber = parseInt(r.questionId);
        return questionNumber >= 9 && questionNumber <= 11;
      });
      const growthAverage = growthResponses.length > 0
        ? Math.round((growthResponses.reduce((sum, r) => sum + r.answer, 0) / growthResponses.length) * 100) / 100
        : 0;

      // Work Environment (items 12-16) - 1-5 scale
      const workEnvironmentResponses = assessmentResponses.filter(r => {
        const questionNumber = parseInt(r.questionId);
        return questionNumber >= 12 && questionNumber <= 16;
      });
      const workEnvironmentAverage = workEnvironmentResponses.length > 0
        ? Math.round((workEnvironmentResponses.reduce((sum, r) => sum + r.answer, 0) / workEnvironmentResponses.length) * 100) / 100
        : 0;

      // Overall Engagement Score (items 1-16, average of all Likert items)
      const likertResponses = assessmentResponses.filter(r => parseInt(r.questionId) <= 16);
      const engagementOverall = likertResponses.length > 0
        ? Math.round((likertResponses.reduce((sum, r) => sum + r.answer, 0) / likertResponses.length) * 100) / 100
        : 0;

      // NPS Score (item 34) - 0-10 scale
      const npsResponse = assessmentResponses.find(r => r.questionId === '34');
      const npsScore = npsResponse?.answer || 0;

      return {
        overall: engagementOverall,
        category1: 0,
        category2: 0,
        category3: engagementOverall,
        engagementTotal: engagementOverall,
        jobSatisfactionScore: jobSatisfactionAverage,
        leadershipScore: leadershipAverage,
        growthScore: growthAverage,
        workEnvironmentScore: workEnvironmentAverage,
        npsScore: npsScore
      };
    } else {
      return {
        overall: 0,
        category1: 0,
        category2: 0,
        category3: 0
      };
    }
  };

  const interpretResults = (scores: any, selectedCategory: number) => {
    if (selectedCategory === 1) {
      // CBI interpretation
      const { cbiTotal, personalBurnout, workRelatedBurnout, clientRelatedBurnout } = scores;
      
      let level: 'Low' | 'Moderate' | 'High';
      let interpretation: string;
      let recommendations: string[] = [];
      
      // CBI scores range from 0-100
      // General guidelines: 0-49 = Low, 50-74 = Moderate, 75-100 = High burnout
      
      if (cbiTotal >= 75) {
        level = 'High';
        interpretation = `High Burnout Risk (Overall: ${cbiTotal}/100). You are experiencing severe burnout symptoms across multiple domains. Personal Burnout: ${personalBurnout}/100, Work-related Burnout: ${workRelatedBurnout}/100, Client-related Burnout: ${clientRelatedBurnout}/100. This level of burnout indicates chronic physical and psychological exhaustion that significantly impacts your well-being, work performance, and personal life. Immediate intervention is strongly recommended.`;
        recommendations = [
          '⚠️ PRIORITY: Seek immediate support from a mental health professional or occupational health service',
          'Consider taking medical leave or reducing work hours if possible',
          'Speak with your supervisor or HR about workplace accommodations and support',
          'Evaluate whether your current work situation is sustainable for your health',
          'Prioritize self-care activities: adequate sleep, nutrition, exercise, and relaxation',
          'Set firm boundaries between work and personal time',
          'Consider professional counseling or therapy to develop coping strategies',
          'Explore stress management techniques (mindfulness, meditation, exercise)',
          'Build or strengthen your support network (family, friends, colleagues)',
          'Take regular breaks throughout the workday',
          'Consider whether a job change or career shift might be necessary for your health'
        ];
      } else if (cbiTotal >= 50) {
        level = 'Moderate';
        interpretation = `Moderate Burnout Risk (Overall: ${cbiTotal}/100). You are experiencing moderate levels of burnout. Personal Burnout: ${personalBurnout}/100, Work-related Burnout: ${workRelatedBurnout}/100, Client-related Burnout: ${clientRelatedBurnout}/100. While not yet severe, these symptoms indicate significant stress and exhaustion that could worsen without intervention. You may feel frequently tired, emotionally drained, or frustrated with work. This is a critical time to implement changes before burnout becomes more severe.`;
        recommendations = [
          'Take proactive steps to address burnout before it worsens',
          'Speak with your supervisor about workload, expectations, and support needs',
          'Practice regular stress management and self-care activities',
          'Set clearer boundaries between work and personal time',
          'Take regular breaks and use your vacation time',
          'Consider counseling or coaching to develop better coping strategies',
          'Evaluate which aspects of work are most draining and explore solutions',
          'Build supportive relationships with colleagues',
          'Engage in activities outside work that bring joy and relaxation',
          'Monitor your symptoms and seek professional help if they increase',
          'Practice saying "no" to additional commitments when overwhelmed'
        ];
      } else {
        level = 'Low';
        interpretation = `Low Burnout Risk (Overall: ${cbiTotal}/100). You are currently experiencing low levels of burnout. Personal Burnout: ${personalBurnout}/100, Work-related Burnout: ${workRelatedBurnout}/100, Client-related Burnout: ${clientRelatedBurnout}/100. You generally feel energized, engaged with your work, and able to manage work demands without excessive exhaustion.`;
        recommendations = [
          'Continue current work-life balance practices',
          'Maintain healthy boundaries',
          'Stay aware of early warning signs',
          'Keep engaging in restorative activities',
          'Support colleagues experiencing burnout'
        ];
      }
      
      return { level, interpretation, recommendations };
    } else if (selectedCategory === 2) {
      // UWES interpretation (0-6 scale for assessment display)
      const { uwesTotal, vigorScore, dedicationScore, absorptionScore } = scores;
      
      let level: 'Low' | 'Moderate' | 'High';
      let interpretation: string;
      let recommendations: string[] = [];
      
      if (uwesTotal >= 5.0) {
        level = 'Low';
        interpretation = `High Work Engagement (Overall: ${uwesTotal}/6.0). You demonstrate strong work engagement across all dimensions. Vigor: ${vigorScore}/6.0 - You feel energized, strong, and resilient at work. Dedication: ${dedicationScore}/6.0 - You find your work meaningful, enthusiastic, and inspiring. Absorption: ${absorptionScore}/6.0 - You are fully immersed and engaged when working. This balanced high engagement indicates healthy, sustainable connection to your work with strong motivation and fulfillment.`;
        recommendations = [
          'Continue nurturing what makes your work engaging and meaningful',
          'Share your positive work practices and attitudes with colleagues',
          'Consider mentoring others to help them find similar engagement',
          'Maintain work-life balance even with high engagement to prevent overwork',
          'Stay aware of early signs of burnout despite high engagement',
          'Use your energy and enthusiasm to drive positive change in your workplace',
          'Regularly reassess your goals to maintain challenge and meaning'
        ];
      } else if (uwesTotal >= 4.0) {
        level = 'Moderate';
        interpretation = `Moderate Work Engagement (Overall: ${uwesTotal}/6.0). You experience moderate levels of work engagement. Vigor: ${vigorScore}/6.0, Dedication: ${dedicationScore}/6.0, Absorption: ${absorptionScore}/6.0. You have some positive connection to your work but there may be areas where you feel less energized, inspired, or absorbed. `;
        
        // Provide specific insights based on dimension variations
        if (vigorScore < 3.5 && dedicationScore >= 4.0) {
          interpretation += 'Note: You are mentally inspired (high dedication) but may be physically fatigued (low vigor) - this could indicate burnout risk despite feeling committed to your work.';
        } else if (vigorScore >= 4.0 && dedicationScore < 3.5) {
          interpretation += 'Note: You are physically energized (high vigor) but emotionally disconnected (low dedication) - this could indicate a possible role mismatch or lack of meaningful purpose in your work.';
        } else if (absorptionScore >= 4.0 && (vigorScore < 3.5 || dedicationScore < 3.5)) {
          interpretation += 'Note: You are engaged in tasks (high absorption) but may not feel fulfilled by the work\'s meaning or lack energy - consider whether your work aligns with your values and provides adequate support.';
        }
        
        recommendations = [
          'Identify which aspects of work are most engaging and which are draining',
          'Seek opportunities to increase meaningful and challenging aspects of your role',
          'Discuss with your supervisor about role adjustments or development opportunities',
          'Focus on building energy through adequate rest, exercise, and work-life balance',
          'Connect your daily tasks to larger goals and purpose',
          'Seek feedback and recognition for your contributions',
          'Build supportive relationships with colleagues',
          'Consider skill development or new projects that might reignite enthusiasm'
        ];
      } else if (uwesTotal >= 2.0) {
        level = 'High';
        interpretation = `Low Work Engagement (Overall: ${uwesTotal}/6.0). You are experiencing low levels of work engagement. Vigor: ${vigorScore}/6.0, Dedication: ${dedicationScore}/6.0, Absorption: ${absorptionScore}/6.0. You may feel lacking in energy, find little meaning or inspiration in your work, and struggle to become absorbed in tasks. This low engagement can lead to reduced performance, job dissatisfaction, and increased risk of burnout or turnover.`;
        recommendations = [
          'Evaluate whether your current role aligns with your values, skills, and interests',
          'Speak with your supervisor about concerns and potential changes',
          'Consider whether workload, resources, or support are adequate',
          'Explore opportunities for professional development or new challenges',
          'Assess your overall well-being - low engagement may signal burnout or depression',
          'Consider taking the CBI (Copenhagen Burnout Inventory) to assess burnout levels',
          'Identify small changes that could make work more meaningful or engaging',
          'Seek career counseling if considering a job or career change',
          'Focus on self-care and stress management outside of work',
          'Connect with colleagues or mentors for support and perspective'
        ];
      } else {
        level = 'High';
        interpretation = `Very Low Work Engagement (Overall: ${uwesTotal}/6.0). You are experiencing very low work engagement. Vigor: ${vigorScore}/6.0, Dedication: ${dedicationScore}/6.0, Absorption: ${absorptionScore}/6.0. You likely feel depleted, disconnected, and disengaged from your work. This severe lack of engagement is a serious concern that requires immediate attention.`;
        recommendations = [
          '⚠️ PRIORITY: Assess whether your current work situation is sustainable for your well-being',
          'Strongly consider taking the CBI assessment to evaluate burnout risk',
          'Seek support from a mental health professional or career counselor',
          'Evaluate whether you need to make significant changes (role, organization, career)',
          'Speak honestly with trusted supervisors or HR about your situation',
          'Take any available leave to rest and reassess',
          'Prioritize your health and well-being over job demands',
          'Explore whether this is temporary (due to specific project/situation) or chronic',
          'Consider whether external factors (health, personal life) are impacting engagement',
          'Make a concrete plan for change if the situation doesn\'t improve'
        ];
      }
      
      return { level, interpretation, recommendations };
    } else if (selectedCategory === 3) {
      // Employee Engagement & Climate interpretation
      const { engagementTotal, overallSatisfactionScore, leadershipScore, teamworkScore, growthScore, cultureScore, npsScore } = scores;
      
      let level: 'Low' | 'Moderate' | 'High';
      let interpretation: string;
      let recommendations: string[] = [];
      
      // Score ranges (1-5 scale for Likert items)
      // 1.0-2.4 = Low
      // 2.5-3.9 = Moderate
      // 4.0-5.0 = High
      
      // NPS Classification
      let npsCategory = '';
      if (npsScore >= 9) npsCategory = 'Promoter';
      else if (npsScore >= 7) npsCategory = 'Passive';
      else npsCategory = 'Detractor';
      
      if (engagementTotal >= 4.0) {
        level = 'High';
        interpretation = `High Employee Engagement & Positive Climate (Overall: ${engagementTotal}/5.0). You experience strong positive perceptions across all workplace dimensions:\n\n`;
        interpretation += `• Overall Satisfaction: ${overallSatisfactionScore}/5.0 - High job satisfaction, feeling valued, meaningful work, and healthy work-life balance\n`;
        interpretation += `• Leadership & Management: ${leadershipScore}/5.0 - Strong confidence in leadership, clear vision, good communication, and manager support\n`;
        interpretation += `• Teamwork & Collaboration: ${teamworkScore}/5.0 - Respectful, helpful, trusting team environment with collaborative problem-solving\n`;
        interpretation += `• Personal Growth: ${growthScore}/5.0 - Access to development resources, regular feedback, and clear career path\n`;
        interpretation += `• Company Culture & Values: ${cultureScore}/5.0 - Clear values, respectful treatment, diversity & inclusion, and feeling comfortable being yourself\n\n`;
        interpretation += `Net Promoter Score: ${npsScore}/10 (${npsCategory}) - `;
        if (npsScore >= 9) interpretation += 'You are highly likely to recommend the company, indicating strong loyalty and satisfaction.';
        else if (npsScore >= 7) interpretation += 'You are somewhat likely to recommend the company.';
        else interpretation += 'You are unlikely to recommend the company despite overall high scores in other areas.';
        
        recommendations = [
          'Continue fostering the positive elements of your work experience',
          'Share your positive experiences with colleagues and leadership',
          'Consider taking on mentorship or leadership roles',
          'Provide constructive feedback to help maintain high standards',
          'Stay engaged with company initiatives and culture-building activities',
          'Support new employees in adapting to the positive work environment'
        ];
      } else if (engagementTotal >= 2.5) {
        level = 'Moderate';
        interpretation = `Moderate Employee Engagement & Mixed Climate (Overall: ${engagementTotal}/5.0). You experience moderate perceptions with areas of strength and concern:\n\n`;
        interpretation += `• Overall Satisfaction: ${overallSatisfactionScore}/5.0\n`;
        interpretation += `• Leadership & Management: ${leadershipScore}/5.0\n`;
        interpretation += `• Teamwork & Collaboration: ${teamworkScore}/5.0\n`;
        interpretation += `• Personal Growth: ${growthScore}/5.0\n`;
        interpretation += `• Company Culture & Values: ${cultureScore}/5.0\n\n`;
        
        // Identify areas of concern
        const concerns: string[] = [];
        if (overallSatisfactionScore < 3.0) concerns.push('Overall Satisfaction');
        if (leadershipScore < 3.0) concerns.push('Leadership & Management');
        if (teamworkScore < 3.0) concerns.push('Teamwork & Collaboration');
        if (growthScore < 3.0) concerns.push('Personal Growth & Development');
        if (cultureScore < 3.0) concerns.push('Company Culture & Values');
        
        if (concerns.length > 0) {
          interpretation += `Areas needing attention: ${concerns.join(', ')}.\n\n`;
        }
        
        interpretation += `Net Promoter Score: ${npsScore}/10 (${npsCategory})`;
        
        recommendations = [
          'Identify specific areas causing dissatisfaction and communicate with management',
          'Seek clarification on career development opportunities and pathways',
          'Provide feedback through appropriate channels about areas needing improvement',
          'Connect with colleagues to build stronger supportive relationships',
          'Request more regular feedback and recognition from your manager',
          'Explore ways to increase work meaningfulness and alignment with values',
          'Participate in employee engagement initiatives or committees',
          'Consider discussing work-life balance concerns with HR or your manager'
        ];
      } else {
        level = 'Low';
        interpretation = `Low Employee Engagement & Negative Climate (Overall: ${engagementTotal}/5.0). ⚠️ You experience significant dissatisfaction and concerns across workplace dimensions:\n\n`;
        interpretation += `• Overall Satisfaction: ${overallSatisfactionScore}/5.0 - Low job satisfaction, limited recognition, poor work-life balance\n`;
        interpretation += `• Leadership & Management: ${leadershipScore}/5.0 - Limited confidence in leadership, unclear vision, poor communication\n`;
        interpretation += `• Teamwork & Collaboration: ${teamworkScore}/5.0 - Team dysfunction, limited trust or collaboration\n`;
        interpretation += `• Personal Growth: ${growthScore}/5.0 - Limited development opportunities, unclear career path\n`;
        interpretation += `• Company Culture & Values: ${cultureScore}/5.0 - Unclear values, lack of respect, limited inclusion\n\n`;
        interpretation += `Net Promoter Score: ${npsScore}/10 (${npsCategory}) - You are unlikely to recommend the company, indicating serious concerns about the work environment.\n\n`;
        interpretation += `This low level of engagement and negative climate perception is a significant risk factor for turnover, decreased performance, and negative impact on your well-being.`;
        
        recommendations = [
          '⚠️ PRIORITY: Evaluate whether this work environment is sustainable for your well-being',
          'Document specific concerns and examples of problematic situations',
          'Speak with HR, employee relations, or a trusted leader about your concerns',
          'Consider whether issues are with specific individuals, teams, or systemic organizational problems',
          'Explore internal transfer opportunities if you value the company but not your current role/team',
          'Connect with external mentors or career counselors for objective perspective',
          'Assess your overall mental health - consider taking the CBI or mental health assessments',
          'Update your resume and explore external opportunities if the situation doesn\'t improve',
          'Set a timeline for seeing improvement before making major decisions',
          'Prioritize your health and well-being - no job is worth sacrificing your mental/physical health',
          'Consider whether speaking up about concerns could lead to positive change or put you at risk'
        ];
      }
      
      return { level, interpretation, recommendations };
    } else {
      // Placeholder for other categories
      let level: 'Low' | 'Moderate' | 'High' = 'Moderate';
      let interpretation = 'Assessment completed. Detailed interpretation will be available once questions are configured.';
      let recommendations: string[] = ['Continue focusing on your occupational well-being.'];

      return { level, interpretation, recommendations };
    }
  };

  const handleSubmit = async () => {
    // Filter out the category selection response for validation
    const assessmentResponses = responses.filter(r => r.questionId !== '0');
    if (assessmentResponses.length < totalQuestions) {
      alert('Please answer all questions before submitting.');
      return;
    }

    setIsSubmitting(true);

    try {
      const scores = calculateScores(responses, selectedCategory!);
      const results = interpretResults(scores, selectedCategory!);

      const assessmentData: OccupationalData = {
        responses,
        scores,
        results
      };

      const response = await fetch(API_CONFIG.ENDPOINTS.OCCUPATIONAL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include',
        body: JSON.stringify(assessmentData)
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('SESSION_EXPIRED');
        }
        throw new Error('Failed to submit assessment');
      }

      const result = await response.json();
      
      // Clear Insights page cache to force refresh
      const userId = JSON.parse(localStorage.getItem('user') || '{}').id;
      const insightsCacheKey = userId ? `lifepsensor_insights_cache_${userId}` : 'lifepsensor_insights_cache';
      localStorage.removeItem(insightsCacheKey);
      
      // Notify InsightsPage about the update
      assessmentUpdateNotifier.notifyUpdate('occupational');
      
      // Navigate to completion page
      navigate('/panel/learn/occupational-assessment/completion', {
        state: { assessmentResult: result.data }
      });

    } catch (error) {

      let errorMessage = 'Error submitting assessment. Please try again.';
      if (error instanceof Error && error.message === 'SESSION_EXPIRED') {
        errorMessage = 'Your session has expired. Please log in again to continue.';
      }
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitting) {
    return (
      <div className="assessment-form-page">
        <div className="assessment-container">
          <div className="assessment-left-panel">
            <div className="assessment-header">
              <h1>Occupational Assessment</h1>
              <p>Processing your responses...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if currentQuestionData exists
  if (!currentQuestionData) {
    return (
      <div className="assessment-form-page">
        <div className="assessment-container">
          <div className="assessment-left-panel">
            <div className="assessment-header">
              <h1>Occupational Assessment</h1>
              <p>Loading assessment questions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-form-page">
      <div className="assessment-container">
        {/* Left Panel - Background Image */}
        <div
          className="assessment-left-panel occupational-panel"
        >
          <div className="assessment-header">
            <h1>Occupational Assessment</h1>
            <p className="assessment-subtitle">
              Evaluating work engagement, burnout, and workplace climate
            </p>
            <p className="assessment-tool-info">
              Comprehensive assessment of occupational well-being and work-related stress
            </p>
          </div>
        </div>

        {/* Right Panel - Questions */}
        <div className="assessment-right-panel">
          <div className="assessment-progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
            <span className="progress-text">
              Question {currentQuestion} of {totalQuestions}
            </span>
          </div>

          <div className="question-section">
            {isSelectionQuestion(currentQuestionData) ? (
              <>
                <h2 className="question-title">
                  {currentQuestionData.question}
                </h2>
                <p className="question-instruction">
                  Please select the assessment category you would like to take.
                </p>

                <div className="answer-options selection-options">
                  {currentQuestionData.options.map((option: string, index: number) => (
                    <button
                      key={index}
                      className={`option-button selection-button ${currentResponse?.answer === index ? 'selected' : ''}`}
                      onClick={() => {
                        saveCurrentResponse(index);
                      }}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </>
            ) : isAssessmentQuestion(currentQuestionData) ? (
              <>
                <div className="category-indicator">
                  <span className="category-badge">
                    {getCategoryName(currentQuestionData.category)}
                  </span>
                </div>
                
                <h2 className="question-title">
                  {currentQuestionData.question}
                </h2>
                <p className="question-instruction">
                  {selectedCategory === 1 
                    ? 'Please select the option that best describes your experience.'
                    : selectedCategory === 2
                    ? 'Based on your work experience over the past few weeks, select the response that best reflects your situation.'
                    : 'Please rate how well this statement describes you using the scale below.'}
                </p>

                <div className="answer-options">
                  {answerOptions.map((option) => (
                    <label key={option.value} className="answer-option">
                      <input
                        type="radio"
                        name={`question-${currentQuestion}`}
                        value={option.value}
                        checked={currentResponse?.answer === option.value}
                        onChange={(e) => saveCurrentResponse(parseInt(e.target.value))}
                      />
                      <span className="option-text">{option.label}</span>
                    </label>
                  ))}
                </div>
              </>
            ) : (currentQuestionData as any).type === 'nps' ? (
              <>
                <div className="category-indicator">
                  <span className="category-badge">
                    {(currentQuestionData as any).category ? getCategoryName((currentQuestionData as any).category) : 'Assessment'}
                  </span>
                </div>
                
                <h2 className="question-title">
                  {(currentQuestionData as any).question}
                </h2>
                <p className="question-instruction">
                  0 = Not likely &nbsp;&nbsp;|&nbsp;&nbsp; 10 = Very likely
                </p>

                <div className="answer-options scale-options">
                  {answerOptions.map((option) => (
                    <label key={option.value} className="scale-option">
                      <input
                        type="radio"
                        name={`question-${currentQuestion}`}
                        value={option.value}
                        checked={currentResponse?.answer === option.value}
                        onChange={(e) => saveCurrentResponse(parseInt(e.target.value))}
                      />
                      <span className="scale-value">{option.label}</span>
                    </label>
                  ))}
                </div>
              </>
            ) : null}
          </div>

          {/* Hide navigation buttons on category selection page */}
          {selectedCategory !== null && (
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={() => {
                  if (currentQuestion === 1 && selectedCategory === null) {
                    navigate('/panel/learn/');
                    return;
                  }
                  if (currentQuestion === 1 && selectedCategory !== null) {
                    setSelectedCategory(null);
                    setCurrentQuestion(1);
                    return;
                  }
                  handleBack();
                }}
                disabled={false}
              >
                Back
              </button>
              {!(currentQuestion === 1 && selectedCategory === null) && (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={!currentResponse}
                >
                  {(currentQuestion === totalQuestions && selectedCategory !== null) ? 'Complete Assessment' : 'Next'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OccupationalAssessmentFormPage;



